import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.results)) return value.results;
  return [];
}

function flattenServices(grouped) {
  const rows = [];
  Object.entries(grouped || {}).forEach(([catalog, services]) => {
    if (!Array.isArray(services)) return;
    services.forEach((service) => rows.push({ ...service, catalog }));
  });
  return rows;
}

function todayIso() {
  return new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

export default function BookingApprovals() {
  const [requests, setRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [rowsByRequest, setRowsByRequest] = useState({});
  const [metaByRequest, setMetaByRequest] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const [requestsRes, servicesRes] = await Promise.all([
        fetchWithAuth(`/api/booking-requests/?status=${encodeURIComponent(statusFilter)}`),
        fetchWithAuth("/api/services/"),
      ]);
      const requestJson = await requestsRes.json().catch(() => []);
      const servicesJson = await servicesRes.json().catch(() => ({}));
      const requestList = toArray(requestJson);
      setRequests(requestList);
      setServices(flattenServices(servicesJson));

      const nextRows = {};
      const nextMeta = {};
      requestList.forEach((request) => {
        nextMeta[request.id] = { date: todayIso(), time: "11:00", staff_note: "" };
        nextRows[request.id] = (request.services || []).map((item, index) => ({
          request_service_id: item.id,
          service_id: item.service_id || "",
          requested_name: item.requested_name,
          staff_id: "",
          start_time: index === 0 ? "11:00" : "",
          duration: item.duration || 30,
          staff_options: item.staff_options || [],
        }));
      });
      setRowsByRequest(nextRows);
      setMetaByRequest(nextMeta);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const serviceById = useMemo(() => {
    const map = {};
    services.forEach((service) => {
      map[String(service.id)] = service;
    });
    return map;
  }, [services]);

  function updateMeta(requestId, patch) {
    setMetaByRequest((prev) => ({
      ...prev,
      [requestId]: { ...(prev[requestId] || {}), ...patch },
    }));
  }

  function updateRow(requestId, index, patch) {
    setRowsByRequest((prev) => {
      const rows = [...(prev[requestId] || [])];
      rows[index] = { ...rows[index], ...patch };
      return { ...prev, [requestId]: rows };
    });
  }

  async function loadStaffOptions(requestId, index, serviceId, date) {
    if (!serviceId) return;
    const res = await fetchWithAuth(
      `/api/staff/by-service/${serviceId}/?date=${encodeURIComponent(date || todayIso())}`
    );
    const options = await res.json().catch(() => []);
    updateRow(requestId, index, { staff_options: toArray(options), staff_id: "" });
  }

  async function approve(request) {
    const meta = metaByRequest[request.id] || {};
    const rows = rowsByRequest[request.id] || [];
    if (!meta.date || !meta.time) {
      setMessage("Date and base time are required.");
      return;
    }
    if (!rows.length || rows.some((row) => !row.service_id || !row.staff_id)) {
      setMessage("Each requested service needs a resolved service and assigned staff.");
      return;
    }

    const payload = {
      date: meta.date,
      time: meta.time,
      staff_note: meta.staff_note || "",
      services: rows.map((row, index) => ({
        request_service_id: row.request_service_id,
        service_id: Number(row.service_id),
        staff_id: Number(row.staff_id),
        start_time: row.start_time || (index === 0 ? meta.time : ""),
      })),
    };

    const res = await fetchWithAuth(`/api/booking-requests/${request.id}/approve/`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      setMessage(json.error || "Approval failed.");
      return;
    }
    setMessage(`Booking request #${request.id} approved.`);
    await load();
  }

  async function reject(request) {
    const meta = metaByRequest[request.id] || {};
    const res = await fetchWithAuth(`/api/booking-requests/${request.id}/status/`, {
      method: "POST",
      body: JSON.stringify({ status: "rejected", staff_note: meta.staff_note || "" }),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      setMessage(json.error || "Reject failed.");
      return;
    }
    setMessage(`Booking request #${request.id} rejected.`);
    await load();
  }

  if (loading) return <div>Loading booking requests...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Booking Approvals</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button className="btn" type="button" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {message && <div style={{ marginBottom: 12 }}>{message}</div>}
      {!requests.length && <div>No booking requests.</div>}

      {requests.map((request) => {
        const rows = rowsByRequest[request.id] || [];
        const meta = metaByRequest[request.id] || {};
        return (
          <div key={request.id} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <div>
                <strong>#{request.id}</strong> {request.customer_name} - {request.phone}
                <div style={{ color: "#666", marginTop: 4 }}>
                  Preferred: {request.preferred_datetime_text || "Not specified"}
                </div>
              </div>
              <strong>{request.status}</strong>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <label>
                Date
                <input
                  type="date"
                  value={meta.date || ""}
                  onChange={(event) => updateMeta(request.id, { date: event.target.value })}
                />
              </label>
              <label>
                Base time
                <input
                  type="time"
                  value={meta.time || ""}
                  onChange={(event) => updateMeta(request.id, { time: event.target.value })}
                />
              </label>
              <label style={{ flex: "1 1 260px" }}>
                Staff note
                <input
                  value={meta.staff_note || ""}
                  onChange={(event) => updateMeta(request.id, { staff_note: event.target.value })}
                  placeholder="Optional note"
                />
              </label>
            </div>

            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Requested</th>
                    <th style={{ textAlign: "left" }}>Service</th>
                    <th style={{ textAlign: "left" }}>Staff</th>
                    <th style={{ textAlign: "left" }}>Start</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.request_service_id || index}>
                      <td>{row.requested_name}</td>
                      <td>
                        <select
                          value={row.service_id || ""}
                          onChange={(event) => {
                            const serviceId = event.target.value;
                            updateRow(request.id, index, {
                              service_id: serviceId,
                              duration: serviceById[String(serviceId)]?.duration || row.duration,
                            });
                            loadStaffOptions(request.id, index, serviceId, meta.date);
                          }}
                        >
                          <option value="">Select service</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.catalog} - {service.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={row.staff_id || ""}
                          onChange={(event) => updateRow(request.id, index, { staff_id: event.target.value })}
                        >
                          <option value="">Select staff</option>
                          {(row.staff_options || []).map((staff) => (
                            <option key={staff.id} value={staff.id}>
                              {staff.name}
                              {staff.working_start ? ` (${staff.working_start}-${staff.working_end})` : ""}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="time"
                          value={row.start_time || ""}
                          onChange={(event) => updateRow(request.id, index, { start_time: event.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {request.status === "pending" && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button className="btn" type="button" onClick={() => approve(request)}>
                  Approve & Create Appointment
                </button>
                <button className="btn" type="button" onClick={() => reject(request)}>
                  Reject
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
